import Edit from "@mui/icons-material/Edit";
import { Box, Button, Tooltip } from "@mui/material";
import { GridActionsCellItem, GridSortModel } from "@mui/x-data-grid-premium";
import { StatusChip } from "components/StatusChip";
import { isPortalInDowntime } from "encamp-shared/src/portal/isPortalInDowntime";
import { PortalSearchQuery } from "generated-graphql/graphql";
import { GridSelectionProvider, OmnisearchDataGridV2 } from "hooks/omnisearch-v2";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { OmnisearchGridColDef } from "hooks/useOmnisearchDatagrid";
import { useSelectedIdInUrl } from "hooks/useSelectedIdInUrl";
import { DateTime } from "luxon";
import { useCallback, useMemo, useState } from "react";

import { PORTAL_SEARCH } from "./api";
import { BulkActionsMenu } from "./BulkActionsMenu";
import { CreateEditPortalDialog } from "./CreateEditDialog";

export type Row = PortalSearchQuery["portalSearch"]["items"][number];

const formatDowntimeBound = (iso: string) => DateTime.fromISO(iso).toFormat("MMM d, h:mm a");

/**
 * Shows Active/Upcoming chips for the portal's downtime window; filters on the
 * synthetic `downtime` omnisearch key (resolved server-side in portalSearch).
 */
const DOWNTIME_COLUMN: OmnisearchGridColDef<Row> = {
  field: "downtime",
  headerName: "Downtime",
  width: 110,
  sortable: false,
  filterKeyType: "enum",
  enumValues: ["ACTIVE", "UPCOMING"],
  valueGetter: (_, row) => {
    if (!row.downtimeWindowStart || !row.downtimeWindowEnd) {
      return "";
    }
    return `${formatDowntimeBound(String(row.downtimeWindowStart))} – ${formatDowntimeBound(
      String(row.downtimeWindowEnd)
    )}`;
  },
  renderCell: ({ row, formattedValue }) => {
    if (!row.downtimeWindowStart || !row.downtimeWindowEnd) {
      return "-";
    }
    const now = new Date();
    const downtimeWindowStart = new Date(String(row.downtimeWindowStart));
    const downtimeWindowEnd = new Date(String(row.downtimeWindowEnd));
    if (downtimeWindowEnd <= now) {
      return "-";
    }
    const active = isPortalInDowntime({ downtimeWindowStart, downtimeWindowEnd }, now);
    return (
      <Tooltip title={formattedValue}>
        <StatusChip color={active ? "warning" : "info"} label={active ? "Active" : "Upcoming"} />
      </Tooltip>
    );
  },
};

const getItems = (data: PortalSearchQuery) => data.portalSearch.items ?? [];
const getCount = (data: PortalSearchQuery) => data.portalSearch.count ?? 0;

const buildVariables = (
  omnisearch: string,
  pagination: { page: number; pageSize: number },
  sort: GridSortModel
) => ({
  search: omnisearch,
  page: pagination.page,
  pageSize: pagination.pageSize,
  sort: sort.map((s) => ({ field: s.field, sort: s.sort })),
});

type PortalsProps = {
  initialPageSize?: number;
};

export const Portals: React.FC<PortalsProps> = ({ initialPageSize }) => {
  const { selectedId: editPortalId, pushIdToUrl, popIdFromUrl } = useSelectedIdInUrl("portalId");

  const [addDialogOpen, setAddDialogOpen] = useState(false);

  useBreadcrumb([
    {
      label: "Workflow Config",
      to: "/staff/workflow-config",
    },
    {
      label: "Portals",
    },
  ]);

  const columns: OmnisearchGridColDef<Row>[] = useMemo(
    () => [
      { field: "description", headerName: "Description", flex: 2 },
      { field: "type", headerName: "Type", width: 100 },
      { field: "jurisdiction", headerName: "Jurisdiction", flex: 1 },
      {
        field: "portalTech",
        headerName: "Portal Tech",
        flex: 1,
        valueGetter: (_, row) => row.portalTech ?? "-",
      },
      DOWNTIME_COLUMN,
      { field: "url", headerName: "URL", flex: 1 },
      { field: "isActive", headerName: "Active", width: 80, type: "boolean" },
      {
        field: "verifiable",
        headerName: "Verifiable",
        width: 90,
        type: "boolean",
      },
      {
        field: "requiresCredentials",
        headerName: "Requires Credentials",
        width: 160,
        type: "boolean",
      },
      { field: "capacity", headerName: "Capacity", width: 90 },
      {
        field: "actions",
        type: "actions",
        align: "right",
        getActions: ({ row }) => [
          <Tooltip title="Edit Portal" key="edit">
            <GridActionsCellItem
              label="Edit Portal"
              onClick={() => pushIdToUrl(row.id)}
              icon={<Edit />}
            />
          </Tooltip>,
        ],
      },
    ],
    [pushIdToUrl]
  );

  const onRowClick = useCallback(({ row }: { row: Row }) => pushIdToUrl(row.id), [pushIdToUrl]);

  const commandButtons = useMemo(
    () => [
      <BulkActionsMenu key="bulk-actions" />,
      <Button key="add-portal-button" variant="contained" onClick={() => setAddDialogOpen(true)}>
        Add Portal
      </Button>,
    ],
    []
  );

  return (
    <Box sx={{ pt: 3 }}>
      <GridSelectionProvider>
        <OmnisearchDataGridV2
          persistenceKey="portals"
          columns={columns}
          query={PORTAL_SEARCH}
          getItems={getItems}
          getCount={getCount}
          buildVariables={buildVariables}
          noRowsMessage="No portals found."
          initialPageSize={initialPageSize ?? 25}
          onRowClick={onRowClick}
          checkboxSelection
          disableRowSelectionOnClick
          commandButtons={commandButtons}
        />
      </GridSelectionProvider>
      {editPortalId && (
        <CreateEditPortalDialog onClose={popIdFromUrl} mode="edit" portalId={editPortalId} />
      )}
      {addDialogOpen && (
        <CreateEditPortalDialog onClose={() => setAddDialogOpen(false)} mode="add" />
      )}
    </Box>
  );
};
